
import { LightningElement, api } from 'lwc';

import {
    signIn,
    signUp,
    confirmSignUp,
    resendSignUpCode,
    resetPassword, 
    confirmResetPassword
} from 'aws-amplify/auth';

export default class getWetSignUpModal extends LightningElement {

    _isLoading = false;
    _isModalOpen = false;
    @api open(options = false) {
        const startInSignUpFlow = options === true || options?.signUp === true;

        this.stage = 'email';   // 'email' | 'signIn' | 'signUp' | 'confirm'
        this.isSignUpFlow = startInSignUpFlow;
        this.existingAccount = false;
        this.email = '';
        this.password = '';
        this.code = '';
        this.firstName = '';
        this.lastName = '';
        this.error = '';
        this._isLoading = false;
        this.info = '';
        this.showResetCodeForm = false;
        this.verificationCode = '';
        this.newPassword = '';
        this._emailFocused = false;
        this._signInPasswordFocused = false;
        this._isModalOpen = true;
    }
    @api close() {
        this._isModalOpen = false;
    }

    stage = 'email';   // 'email' | 'signIn' | 'signUp' | 'confirm'
    email = '';
    password = '';
    code = '';
    firstName = '';
    lastName = '';
    error = '';    
    info = '';
    isSignUpFlow = false;
    existingAccount = false;


    get userModalTitle() {
        if (this.stage === 'email') {
            return this.isSignUpFlow ? 'Sign up' : 'Log in or sign up';
        }
        if (this.stage === 'signIn') {
            return 'Log in';
        }
        if (this.stage === 'signUp') {
            return 'Create your account';
        }
        if (this.stage === 'confirm') {
            return 'Confirm your email';
        }
    }

    get isEmailStage() { return this.stage === 'email'; }
    get isSignInStage() { return this.stage === 'signIn'; }
    get isSignUpStage() { return this.stage === 'signUp'; }
    get isConfirmStage() { return this.stage === 'confirm'; }
    get emailContinueLabel() { return this.isSignUpFlow ? 'Continue' : 'Log in'; }


    _signInPasswordFocused = false;
    _emailFocused = false;
    renderedCallback() {
        if (this.isEmailStage && !this._emailFocused) {
            const emailInput = this.template.querySelector('[data-id="email"][data-stage="email"]');
            if (emailInput) {
                emailInput.focusMe();
                this._emailFocused = true;
            }
        }
        else if (this.isSignInStage && !this._signInPasswordFocused) {
            const passwordInput = this.template.querySelector('[data-id="password"][data-stage="sign-in"]');
            if (passwordInput) {
                passwordInput.focusMe();
                this._signInPasswordFocused = true;
            }
        }
    }   

    handleInputChange(event) {
        const { name, value } = event.target;
        this[name] = value;
    }

    handleStartSignUp() {
        this.isSignUpFlow = true;
        this.existingAccount = false;
        this.error = '';
        this.info = 'Enter your email to create an account.';
        this.handleContinueWithEmail();
    }

    // 1) User types email, clicks continue
    handleContinueWithEmail() {
        const emailInput = this.template.querySelector('[data-id="email"]');
        if (emailInput) {
            this.email = emailInput.getValue();
        }
        this.error = '';
        this.info = '';
        if (!this.email) {
            this.error = 'Please enter your email.';
            return;
        }
        this.email = this.email.trim();
        this.existingAccount = false;
        this.password = '';

        if (this.isSignUpFlow) {
            this.stage = 'signUp';
            this.info = 'Choose a password to create your account.';
            return;
        }

        this.stage = 'signIn';
        this.info = 'Enter your password to log in.';
    }

    // 2) Try to sign in with email + password
    async handleSignIn() {
        this.error = '';
        this.info = '';

        const passInput = this.template.querySelector('[data-id="password"]');
        if (passInput) {
            this.password = passInput.getValue();
            //console.log('here is password: ', this.password);
        } 

        if (!this.password) {
            this.info = 'Enter a password';
            return;
        }

        this._isLoading = true;
        try {
            const res = await signIn({
                username: this.email,
                password: this.password
            });

            // Amplify v6: res has { isSignedIn, nextStep, ... }
            if (res?.isSignedIn) {
                this.dispatchEvent(
                    new CustomEvent('userauthenticated', {
                        detail: { user: res }
                    })
                );
                return;
            }

            // If it's not signed in, check nextStep
            if (res?.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                // Needs confirmation
                this.stage = 'confirm';
                this.code = '';
                this.info = 'Your account is not confirmed. Enter the confirmation code we sent to your email.';
                return;
            }

            // Fallback for weird cases
            this.error = 'Could not complete sign-in. Try again.';
        } catch (e) {
            //console.log('signIn error', e, e.name, e.message);

            if (e.name === 'UserNotFoundException') {
                // Email never registered → sign-up
                this.stage = 'signUp';
                this.isSignUpFlow = true;
                this.info = 'No account found with that email. Create one by setting a password.';
                this.password = '';
            } else if (e.name === 'NotAuthorizedException') {
                // Bad password
                this.error = 'Incorrect password. Try again.';
            } else if (e.name === 'UserNotConfirmedException') {
                // Exists but unconfirmed → go to confirm and (try to) resend code
                this.stage = 'confirm';
                this.code = '';
                try {
                    await resendSignUpCode({ username: this.email });
                    this.info = 'Your account is not confirmed. We just sent you a new confirmation code.';
                } catch (resendErr) {
                    //console.log('resendSignUpCode error', resendErr);
                    this.info = 'Your account is not confirmed. Enter the confirmation code you received earlier.';
                }
            } else if (e.name === 'UserUnAuthenticatedException') {
                // This is that cursed case — treat it like "sign-in failed, try sign-up/confirm"
                this.stage = 'signUp';
                this.isSignUpFlow = true;
                this.info = 'We could not sign you in with that password. If you don’t already have an account, create one now.';
                this.password = '';
            } else {
                this.error = e.message || 'Error signing in.';
            }
        } finally {
            this._isLoading = false;
        }
    }

    // 3) Sign up new user
    async handleSignUp() {
        this.error = '';
        this.info = '';
        this.existingAccount = false;

        const firstNameEle = this.template.querySelector('[data-id="signupFirstName"]');
        if (firstNameEle) {
            this.firstName = firstNameEle.getValue();
        }

        const lastNameEle = this.template.querySelector('[data-id="signupLastName"]');
        if (lastNameEle) {
            this.lastName = lastNameEle.getValue();
        }

        const signupPassEle = this.template.querySelector('[data-id="signupPassword"]');
        if (signupPassEle) {
            this.password = signupPassEle.getValue();
            //console.log('here is password in handleSignUp() of getWetSignUpModal.js: ', this.password, ' and here is email: ', this.email);
        }

        this.firstName = (this.firstName || '').trim();
        this.lastName = (this.lastName || '').trim();
        if (!this.firstName || !this.lastName) {
            this.error = 'First Name and Last Name are required.';
            return;
        }

        if (!this.password) {
            this.info = 'Enter a password.';
            return;
        }
        this._isLoading = true;

        try {
            await signUp({
                username: this.email,
                password: this.password,
                options: {
                    userAttributes: {
                        email: this.email,
                        given_name: this.firstName,
                        family_name: this.lastName
                    }
                }
            });

            this.stage = 'confirm';
            this.code = '';
            this.info = 'We sent you a confirmation code. Enter it below to verify your email.';
        } catch (e) {
            //console.log('signUp error', e);

            if (e.name === 'UsernameExistsException') {
                this.existingAccount = true;
                this.password = '';
                this.error = 'An account with this email already exists.';
                this.info = 'Forgot your password? Reset it to regain access.';
            } else {
                this.error = e.message || 'Error signing up.';
            }
        } finally {
            this._isLoading = false;
        }
    }

    // ?4) resend code if not received
    async handleResendCode() {
        this.error = '';
        this.info = '';
        this._isLoading = true;
        //console.log('here is email in handleResendCode() f getWetSignUpModal.js: ', this.email);
        try {
            const res = await resendSignUpCode({ username: this.email });
            //console.log('resendSignUpCode result:', res);
            this.info = 'We sent a new confirmation code to your email.';
        } catch (e) {
            console.error(
                'resendSignUpCode error:',
                e,
                'name:',
                e?.name,
                'message:',
                e?.message
            );

            if (e.name === 'LimitExceededException') {
                this.error = 'Too many attempts. Wait a bit before requesting another code.';
            } else if (e.name === 'UserNotFoundException') {
                this.error = 'No pending account found for that email. Try signing up again.';
            } else if (e.name === 'CodeDeliveryFailureException') {
                this.error = 'Cognito could not send the email. Check your user pool email configuration.';
            } else {
                this.error = e.message || 'Error resending code.';
            }
        } finally {
            this._isLoading = false;
        }
    }

    // 4) Confirm email for newly signed-up user
    async handleConfirm() {
        this.error = '';
        this.info = '';

        const confirmCodeEle = this.template.querySelector('[data-id="confirmationCode"]');
        if (confirmCodeEle) {
            this.code = confirmCodeEle.getValue();
            //console.log('here is confrim code in handleConfirm() of getWetSignUpModal.js: ', this.code);
        } 

        try {
            const code = (this.code || '').trim();
            if (!code) {
                this.info = 'Enter the code from your email.';
                return;
            }

            this._isLoading = true;

            await confirmSignUp({
                username: this.email,
                confirmationCode: code
            });

            this.stage = 'signIn';
            this.isSignUpFlow = false;
            this.existingAccount = false;
            this.password = '';
            this.code = '';
            this._signInPasswordFocused = false;
            this.info = 'Email verified. Log in with the password you just created.';
        } catch (e) {
            console.error('confirmSignUp error:', e, e?.name, e?.message);
            this.error = e.message || 'Error confirming code.';
        } finally {
            this._isLoading = false;
        }
    }

    showResetCodeForm = false;    
    verificationCode = '';
    newPassword = '';
    async handleForgotPassword() {
        this.error = '';
        this.info = '';

        try {
            if (!this.email) {
                this.info = 'Enter your email to reset your password';
                return;
            }   
            this._isLoading = true;
            const res = await resetPassword({
                username: this.email
            });

            // Amplify v6: res.nextStep.signInStep
            //console.log('resetPassword result: ', JSON.stringify(res, null, 2));

            // Usually goes to CONFIRM_RESET_PASSWORD_WITH_CODE
            this.info = 'We sent a verification code to your email.';
            this.showResetCodeForm = true;  // toggle UI for code + new password
        } catch (e) {
            console.error('resetPassword error: ', e);
            this.error = e.message || 'Error starting password reset.';
        } finally {
            this._isLoading = false;
        }
    }

    handleConfirmCode() {
        this.error = '';
        const inputEle = this.template.querySelector('[data-id="verification-code"]');

        if (inputEle) {
            this.verificationCode = (inputEle.getValue() || '').trim();
        }

        if (!this.verificationCode) {
            this.error = 'Enter the verification code from your email.';
            return;
        }

        this.info = 'Enter a new password.';
    }

    async handleResendResetCode() {
        await this.handleForgotPassword();
    }

    async handleConfirmResetPassword() {
        this.error = '';
        this.info = '';

        const passInput = this.template.querySelector('[data-id="new-password"]');

        if (passInput) {
            this.newPassword = passInput.getValue?.() ?? passInput.value;
        }

        if (!this.verificationCode || !this.newPassword) {
            this.info = 'Enter the code and your new password.';
            return;
        }
        this._isLoading = true;
        try {
            const res = await confirmResetPassword({
                username: this.email,
                confirmationCode: this.verificationCode,
                newPassword: this.newPassword
            });

            //console.log('confirmResetPassword result: ', JSON.stringify(res, null, 2));

            // If no error, reset is done
            this.showResetCodeForm = false;
            this.stage = 'signIn';
            this.isSignUpFlow = false;
            this.existingAccount = false;
            this.password = '';
            this.newPassword = '';
            this.verificationCode = '';
            this._signInPasswordFocused = false;
            this.info = 'Password reset successful. Log in with your new password.';
        } catch (e) {
            console.error('confirmResetPassword error: ', e);
            this.error = e.message || 'Error completing password reset.';
        } finally {
            this._isLoading = false;
        }
    }



    backToEmail() {
        this.stage = 'email';
        this.password = '';
        this.code = '';
        this.firstName = '';
        this.lastName = '';
        this.error = '';
        this.info = '';
        this.isSignUpFlow = false;
        this.existingAccount = false;
        this.showResetCodeForm = false;
        this.verificationCode = '';
        this.newPassword = '';
        this._emailFocused = false;
        this._signInPasswordFocused = false;
    }

}
